import React, { useState } from 'react';
import { Dialog, DialogActions, Grid } from '@material-ui/core';
import { useTranslation } from '@michelin/central-provider';
import {
  TextField, Button, Select, useDialogs,
} from '@michelin/acid-components';
import {
  useForm, Controller,
} from 'react-hook-form';
import { getOptions } from '@michelin/select-options-provider';
import PreventTransitionDialog from '@michelin/acid-components/build/components/PreventTransitions/PreventTransitionDialog';
import { ClosableDialogTitle } from 'components/ClosableDialog/ClosableDialogTitle';
import { ClosableDialogContent } from 'components/ClosableDialog/ClosableDialogContent';
import { scrollToDisplayFirstError } from 'Utils';
import { MechanicOutsideCertification } from '../../../Types';
import FileUpload from '../../FileUpload/FileUpload';

interface MechanicOutsideCertificationsDetailsProps {
  action: string,
  defaultValues: MechanicOutsideCertification,
  onSave: (data:MechanicOutsideCertification) => void,
  onClose: () => void,
}

function MechanicOutsideCertificationsDetail(props:MechanicOutsideCertificationsDetailsProps) {
  const { t } = useTranslation();
  const { errorDialog } = useDialogs();

  const {
    register, formState: { errors, isDirty, isSubmitting }, control, watch, handleSubmit,
  } = useForm<any>({ defaultValues: props.defaultValues });

  const [preventTransition, setPreventTransition] = useState<boolean>(false);
  const [uploading, setUploading] = useState<boolean>(false);

  const options = getOptions('spp_mechanical_outside_certifications_type');

  const typeName = watch('type');

  const save = async (data: MechanicOutsideCertification) => {
    props.onSave(data);
  };

  const onError = () => {
    errorDialog(
      t('Please check the marked fields.').split('\n'),
      t('Error saving certification information.'),
    );
    scrollToDisplayFirstError();
  };

  return (
    <>
      <Dialog
        onClose={() => {
          if (isDirty || uploading || isSubmitting) setPreventTransition(true);
          else props.onClose();
        }}
        aria-labelledby="customized-dialog-title"
        open
        fullWidth
        maxWidth="sm"
        scroll="paper"
      >
        <ClosableDialogTitle
          onClose={() => {
            if (isDirty || uploading || isSubmitting) setPreventTransition(true);
            else props.onClose();
          }}
        >
          {t('Mechanical Training (Outside Certifications of Training)')}
        </ClosableDialogTitle>

        <ClosableDialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} style={{ marginTop: 7 }}>
              <Controller
                control={control}
                name="type"
                rules={{ required: true }}
                render={({ field }) => (
                  <Select
                    id="type"
                    label={t('Type of Training')}
                    options={options}
                    error={errors.type}
                    readOnly={false}
                    required
                    fullWidth
                  /* eslint-disable-next-line react/jsx-props-no-spreading */
                    {...field}
                  />
                )}
              />
            </Grid>
            {typeName === 'other'
              ? (
                <Grid item xs={12} style={{ marginTop: 7 }}>
                  <TextField
                    label={t('Other Training')}
                    error={errors.other_type_name}
                    inputProps={{
                      ...register('other_type_name', {
                        required: typeName === 'other',
                      }),
                    }}
                    readOnly={false}
                    required={typeName === 'other'}
                  />
                </Grid>
              )
              : null}
            <Grid item xs={12} style={{ marginTop: 7 }}>
              <TextField
                label={t('Provided By')}
                error={errors.provided_by}
                inputProps={{
                  ...register('provided_by', {
                    required: true,
                  }),
                }}
                readOnly={false}
                required
              />
            </Grid>
            <Grid item xs={12} style={{ marginTop: 7 }}>
              <Controller
                control={control}
                /* eslint-disable-next-line react/jsx-props-no-spreading */
                {...register('file', { required: true })}
                render={(item) => (
                  <FileUpload
                    error={errors.file}
                    data={item.field.value}
                    onChange={item.field.onChange}
                    onUploading={setUploading}
                    label={t('Certification Documents')}
                    required
                    readOnly={false}
                    mode="minimal"
                  />
                )}
              />
            </Grid>
          </Grid>
        </ClosableDialogContent>
        <DialogActions>
          <Button
            size="small"
            color="primary"
            style={{ margin: '8px' }}
            disabled={!isDirty || uploading}
            onClick={handleSubmit(save, onError)}
          >
            {props.action === 'edit' ? t('Update') : t('Add')}
          </Button>
          <Button
            size="small"
            color="default"
            style={{ margin: '8px' }}
            disabled={isSubmitting || uploading}
            onClick={() => {
              if (isDirty) setPreventTransition(true);
              else props.onClose();
            }}
          >
            {t('Cancel')}
          </Button>
        </DialogActions>
      </Dialog>
      <PreventTransitionDialog
        open={preventTransition}
        handleCancel={() => { setPreventTransition(false); }}
        handleClose={() => { setPreventTransition(false); props.onClose(); }}
        handleDiscard={() => { setPreventTransition(false); props.onClose(); }}
        message={t('You have unsaved changes...')}
        title={t('Do you want to discard those changes?')}
      />
    </>
  );
}

export {
  MechanicOutsideCertificationsDetail,
};
